import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import cn from "classnames";

import './ButtonDropdown.scss';
import { Button, IButtonProps } from 'components';
import { usePopper } from 'react-popper';

export interface IButtonDropdownProps {
    buttonContent?: ReactNode,
    buttonProps?: Omit<IButtonProps, 'onClick' | 'ref'>,
    className?: string,
    children: ReactNode,
}

export interface IButtonDropdownContext {
    close: () => void,
}

export const ButtonDropdownContext = React.createContext({close: undefined});

export function ButtonDropdown({
    buttonContent,
    buttonProps,
    className,
    children,
}: IButtonDropdownProps) {
    const [popReferenceEl, setPopReferenceEl] = useState(null);
    const [popEl, setPopEl] = useState(null);
    const pop = usePopper(popReferenceEl, popEl, {
        placement: 'bottom-start',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 2],
                },
            }
        ]
    });
    const [isOpen, setIsOpen] = useState(false);

    function onClickButton() {
        setIsOpen((val) => {
            return !val;
        })
    }

    const onClickWindow = useCallback((e) => {
        // Ignore clicks from the button or dropdown
        if (isOpen 
            && e.target !== popEl
            && !popEl.contains(e.target)
            && e.target !== popReferenceEl
            && !popReferenceEl.contains(e.target)
        ) {
            setIsOpen(false);
        }
    }, [isOpen, popEl, popReferenceEl]);

    useEffect(() => {
        window.addEventListener('click', onClickWindow);

        return () => {
            window.removeEventListener('click', onClickWindow)
        }
    }, [onClickWindow])

    // Expose a context so that ButtonDropdownItem can close the dropdown when an item is clicked.
    // We're using memo to prevent unnecessary rerenders
    const closeCallback = useCallback(() => {
        setIsOpen(false);
    }, []);

    const ctxVal = useMemo<IButtonDropdownContext>(() => {
        return {
            close: closeCallback,
        };
    }, [closeCallback])

    return (<>
        <Button {...buttonProps} onClick={onClickButton} ref={setPopReferenceEl}>
            {buttonContent}
        </Button>
        {isOpen && (<div
            className="buttonDropdown_dropdown open"
            style={pop.styles.popper}
            ref={setPopEl}
            {...pop.attributes.popper}
        >
            <div className="buttonDropdown_dropdownInner">
                <ButtonDropdownContext.Provider value={ctxVal}>
                    {children}
                </ButtonDropdownContext.Provider>
            </div>
        </div>)}
    </>)
}